import type { paths } from "./generated/admin";
import { ADMIN_BASE_URL, authFetch, createApiClient, unwrap } from "./http";
import type {
  SharedRun,
  SharedRunList,
  AdminAuditEntry,
  AdminAuditList,
  Me,
  Role,
  User,
  UserList,
  UsersSummary,
  UserStatus,
} from "./types";

const client = createApiClient<paths>(ADMIN_BASE_URL);

export interface UserFilters {
  status?: UserStatus;
  q?: string;
  role?: Role;
  cursor?: string;
  limit?: number;
}

export interface AdminAuditFilters {
  actor?: string;
  action?: AdminAuditEntry["action"];
  target?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export interface SharedRunFilters {
  owner?: string;
  result?: string;
  profile?: string;
  cursor?: string;
  limit?: number;
}

const userPath = (id: string) => ({ params: { path: { user_id: id } } });

export const adminApi = {
  me: () => unwrap<Me>(client.GET("/me")),
  users: (q: UserFilters = {}) => unwrap<UserList>(client.GET("/users", { params: { query: q } })),
  user: (id: string) => unwrap<User>(client.GET("/users/{user_id}", userPath(id))),
  summary: () => unwrap<UsersSummary>(client.GET("/users/summary")),
  admit: (id: string, role: Role) => unwrap<User>(client.POST("/users/{user_id}/admit", { ...userPath(id), body: { role } })),
  reject: (id: string) => unwrap<User>(client.POST("/users/{user_id}/reject", userPath(id))),
  changeRole: (id: string, role: Role) => unwrap<User>(client.PATCH("/users/{user_id}", { ...userPath(id), body: { role } })),
  deactivate: (id: string) => unwrap<User>(client.POST("/users/{user_id}/deactivate", userPath(id))),
  reactivate: (id: string, role?: Role) =>
    unwrap<User>(client.POST("/users/{user_id}/reactivate", { ...userPath(id), body: role ? { role } : {} })),
  sharedRun: (runId: string) => unwrap<SharedRun>(client.GET("/shared/runs/{run_id}", { params: { path: { run_id: runId } } })),
  sharedRuns: (q: SharedRunFilters = {}) => unwrap<SharedRunList>(client.GET("/shared/runs", { params: { query: q } })),
  sharedObject: (runId: string, name: string) =>
    authFetch(new Request(`${ADMIN_BASE_URL}/shared/runs/${encodeURIComponent(runId)}/objects/${name}`, { cache: "no-cache" })),
  audit: (q: AdminAuditFilters = {}) => unwrap<AdminAuditList>(client.GET("/audit", { params: { query: q } })),
};
